/**
 * Online / in-app payment request + response schemas (plan 11 §3.4). Shared by
 * the /payments-online Edge handler and the typed client caller. Money is integer
 * minor units (MKD × 100). The same idempotency_key flows initiate -> confirm ->
 * /payments -> fiscal (plan 11 §3.5 idempotency invariant).
 */
import { z } from 'zod';

export const OnlinePaymentMethodSchema = z.enum(['card_online', 'bank_3ds']);

/** POST /payments-online/initiate */
export const InitiateOnlinePaymentRequestSchema = z.object({
  plan_id: z.string().uuid(),
  method: OnlinePaymentMethodSchema,
  idempotency_key: z.string().uuid(),
  return_url: z.string().url(),
  cancel_url: z.string().url(),
  customer_email: z.string().email().optional(),
});
export type InitiateOnlinePaymentRequest = z.infer<typeof InitiateOnlinePaymentRequestSchema>;

export const InitiateOnlinePaymentResponseSchema = z.object({
  session_id: z.string().uuid(),
  status: z.literal('initiated'),
  redirect_url: z.string().url(),
  expires_at: z.string(),
  amount: z.number().int(),
  currency: z.literal('MKD'),
});
export type InitiateOnlinePaymentResponse = z.infer<typeof InitiateOnlinePaymentResponseSchema>;

/** POST /payments-online/confirm */
export const ConfirmOnlinePaymentRequestSchema = z.object({
  session_id: z.string().uuid(),
  idempotency_key: z.string().uuid(),
  /** Raw processor webhook/redirect params; the driver parses + validates these. */
  processor_callback_payload: z.unknown().optional(),
});
export type ConfirmOnlinePaymentRequest = z.infer<typeof ConfirmOnlinePaymentRequestSchema>;

export const OnlinePaymentSessionStatusSchema = z.enum([
  'initiated',
  'captured',
  'completed',
  'failed',
  'cancelled',
  'expired',
]);
export type OnlinePaymentSessionStatus = z.infer<typeof OnlinePaymentSessionStatusSchema>;

export const ConfirmOnlinePaymentResponseSchema = z.object({
  session_id: z.string().uuid(),
  status: OnlinePaymentSessionStatusSchema,
  payment_id: z.string().uuid().nullable(),
  membership_id: z.string().uuid().nullable(),
  fiscal_status: z.enum(['issued', 'failed_retriable', 'failed', 'pending']).nullable(),
  fiscal_receipt_id: z.string().uuid().nullable(),
  device_receipt_number: z.string().nullable(),
  failure_code: z.string().nullable(),
  failure_message: z.string().nullable(),
  retriable: z.boolean().optional(),
});
export type ConfirmOnlinePaymentResponse = z.infer<typeof ConfirmOnlinePaymentResponseSchema>;

/** POST /payments-online/:id/cancel */
export const CancelOnlinePaymentRequestSchema = z.object({
  session_id: z.string().uuid(),
  idempotency_key: z.string().uuid(),
  reason: z.string().min(1).max(500).optional(),
});
export type CancelOnlinePaymentRequest = z.infer<typeof CancelOnlinePaymentRequestSchema>;

/**
 * POST /b2b-invoices (BP-09, plan 11 §3.4 / Task 8). Corporate invoice request.
 * line_items are FiscalLineItem-shaped; totals are recomputed + checked server-side.
 * The resulting fiscal_receipts row carries doc_type='e_invoice' + customer_tax_id.
 */
export const B2BInvoiceLineItemSchema = z.object({
  name: z.string().min(1),
  quantity: z.number().positive(),
  unit_price: z.number().int().positive(), // MKD minor units
  tax_rate_code: z.enum(['A', 'B', 'C', 'EXEMPT']),
  total: z.number().int().positive(),
});
export type B2BInvoiceLineItem = z.infer<typeof B2BInvoiceLineItemSchema>;

export const B2BInvoiceRequestSchema = z.object({
  payment_id: z.string().uuid().optional(), // link to existing payment, OR standalone
  member_id: z.string().uuid().optional(),
  customer_company: z.string().min(1),
  customer_tax_id: z.string().min(1),
  customer_address: z.string().optional(),
  customer_email: z.string().email().optional(),
  line_items: z.array(B2BInvoiceLineItemSchema).min(1),
  idempotency_key: z.string().uuid(),
  notes: z.string().max(2000).optional(),
});
export type B2BInvoiceRequest = z.infer<typeof B2BInvoiceRequestSchema>;

export const B2BInvoiceStatusSchema = z.enum(['draft', 'issued', 'sent', 'voided']);
export type B2BInvoiceStatus = z.infer<typeof B2BInvoiceStatusSchema>;

export const B2BInvoiceViewSchema = z.object({
  id: z.string().uuid(),
  invoice_number: z.string().nullable(),
  customer_company: z.string(),
  customer_tax_id: z.string(),
  customer_email: z.string().nullable(),
  subtotal: z.number().int(),
  tax_total: z.number().int(),
  total: z.number().int(),
  currency: z.literal('MKD'),
  status: B2BInvoiceStatusSchema,
  fiscal_status: z.enum(['pending', 'issued', 'failed_retriable', 'failed']).nullable(),
  fiscal_receipt_id: z.string().uuid().nullable(),
  issued_at: z.string().nullable(),
  sent_at: z.string().nullable(),
  created_at: z.string(),
});
export type B2BInvoiceView = z.infer<typeof B2BInvoiceViewSchema>;

/** GET /payments-online/:id status read shape. */
export const OnlinePaymentSessionViewSchema = z.object({
  session_id: z.string().uuid(),
  status: OnlinePaymentSessionStatusSchema,
  amount: z.number().int(),
  currency: z.literal('MKD'),
  method: OnlinePaymentMethodSchema,
  payment_id: z.string().uuid().nullable(),
  membership_id: z.string().uuid().nullable(),
  fiscal_receipt_id: z.string().uuid().nullable(),
  failure_code: z.string().nullable(),
  failure_message: z.string().nullable(),
  expires_at: z.string().nullable(),
  created_at: z.string(),
});
export type OnlinePaymentSessionView = z.infer<typeof OnlinePaymentSessionViewSchema>;
